import { NavLink, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { motion } from 'motion/react';
import { ArrowLeft, LogOut } from 'lucide-react';
import { NAV } from '../../lib/nav';
import { useAuth } from '../../lib/auth/AuthContext';
import { ROLE_META } from '../../lib/constants';
import { Avatar } from '../ui/Avatar';
import { Tooltip } from '../ui/Tooltip';

export function Sidebar() {
  const { ctx, user, signOut, isImpersonating, stopImpersonation } = useAuth();
  const role = ctx?.user.role;
  const isKaprodi = ctx ? ctx.kaprodiProdiIds.length > 0 : false;
  const nav = role
    ? NAV[role].filter((item) => !(item.to === '/app/dosen/rekap' && !isKaprodi))
    : [];
  const home = ctx ? `/app/${ctx.user.role}` : '/';
  const activePath = ctx ? `/app/${ctx.user.role}` : '';

  return (
    <aside className="sidebar-glass sticky top-0 hidden h-dvh w-60 shrink-0 flex-col md:flex">
      {/* Brand */}
      <div className="flex h-12 shrink-0 items-center gap-2 px-4">
        <Link to={home} className="flex items-center gap-2">
          <span className="grid size-7 place-items-center rounded-none bg-accent text-[14px] font-black text-white">
            B
          </span>
          <span className="text-[15px] font-bold tracking-tight text-sidebar-ink">Bimbingan</span>
        </Link>
      </div>

      {/* Role label */}
      {ctx && (
        <div className="mx-4 mt-1 flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-sidebar-faint">
          <span
            className="size-1.5 rounded-none"
            style={{ backgroundColor: ROLE_META[ctx.user.role].color }}
          />
          {ROLE_META[ctx.user.role].label}
        </div>
      )}

      {/* Primary nav */}
      <nav className="scrollable mt-3 flex-1 overflow-y-auto px-3 pb-4">
        <div className="space-y-0.5">
          {nav.map((item) => {
            const to = item.to;
            const isHome = item.to === activePath;
            return (
              <NavLink
                key={item.to}
                to={to}
                end={isHome}
                className={({ isActive }) =>
                  clsx(
                    'group relative flex h-9 items-center gap-2.5 rounded-none px-2.5 text-[13px] font-semibold transition-colors',
                    isActive
                      ? 'text-sidebar-ink'
                      : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId="sidebar-active"
                        className="pointer-events-none absolute inset-0 rounded-none bg-sidebar-active ring-1 ring-white/10"
                        transition={{ type: 'spring', bounce: 0.15, duration: 0.4 }}
                      />
                    )}
                    <item.icon
                      className={clsx(
                        'relative z-10 size-[17px]',
                        isActive
                          ? 'text-sidebar-active-text'
                          : 'text-sidebar-faint group-hover:text-sidebar-muted',
                      )}
                      strokeWidth={isActive ? 2.4 : 2}
                    />
                    <span className="relative z-10">{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* User footer */}
      <div className="border-t border-sidebar-hairline px-3 py-2.5">
        <div className="flex items-center gap-2.5 rounded-none px-1.5 py-1">
          <Avatar name={user?.name} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-semibold text-sidebar-ink">
              {user?.name ?? 'Pengguna'}
            </div>
            <div className="truncate text-[11px] text-sidebar-faint">
              {role ? ROLE_META[role].short : ''}
            </div>
          </div>
          {isImpersonating ? (
            <Tooltip label="Kembali ke akun admin">
              <button
                onClick={stopImpersonation}
                aria-label="Kembali ke akun admin"
                className="grid size-8 shrink-0 place-items-center rounded-none text-sidebar-faint transition-colors hover:bg-sidebar-hover hover:text-sidebar-active-text press"
              >
                <ArrowLeft className="size-4" />
              </button>
            </Tooltip>
          ) : (
            <Tooltip label="Keluar">
              <button
                onClick={() => void signOut()}
                aria-label="Keluar"
                className="grid size-8 shrink-0 place-items-center rounded-none text-sidebar-faint transition-colors hover:bg-sidebar-hover hover:text-sidebar-danger press"
              >
                <LogOut className="size-4" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </aside>
  );
}
